import React from 'react';
import LoadIndicator from '../LoadIndicator';
import { Link } from 'react-router-dom'; // If you're using React Router
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type IconProp } from '@fortawesome/fontawesome-svg-core';

import './Button.css';
import { IntentType } from '../Intent';


// Define the common props that all variations share
interface BaseProps {
  alignText?: 'left' | 'center' | 'right';
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
  iconLeft?: IconProp;
  iconRight?: IconProp;
  intent?: IntentType;
  loading?: boolean;
  role?: string;
  size?: 'small' | 'default' | 'large';
  width?: string;
  tabIndex?: number;
  text?: string;
  title?: string;
  variant?: 'filled' | 'minimal';
  onClick?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  onMouseEnter?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  onMouseDown?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  onMouseUp?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  htmlAttributes?: React.HTMLAttributes<HTMLButtonElement | HTMLAnchorElement>;
  style?: React.CSSProperties;
}

// Props for a standard HTML button
interface ButtonProps extends BaseProps {
  type?: 'button' | 'submit' | 'reset';
  href?: never;
  to?: never;
}

// Props for an external link (<a> tag)
interface AnchorProps extends BaseProps {
  href: string;
  target?: '_blank' | '_self' | '_parent' | '_top';
  rel?: string;
  to?: never;
}

// Props for a React Router Link
interface RouterLinkProps extends BaseProps {
  to: string;
  replace?: boolean;
  state?: any;
  href?: never;
}

// Union type for the component's props
type ButtonComponentProps = ButtonProps | AnchorProps | RouterLinkProps;

const Button: React.FC<ButtonComponentProps> = ({
  alignText = 'center',
  children,
  className = '',
  disabled = false,
  iconLeft,
  iconRight,
  intent = 'none',
  loading = false,
  role,
  size = 'default',
  tabIndex = 0,
  text,
  title,
  variant = 'filled',
  width = 'unset',
  onClick,
  onMouseEnter,
  onMouseLeave,
  onMouseDown,
  onMouseUp,
  onKeyDown,
  htmlAttributes,
  ...props
}) => {
  const content = text || children;

  const commonProps = {
    className: `button ${className} ${'intent-' + intent} ${'size-' + size} ${'variant-' + variant} ${'align-text-' + alignText} ${disabled ? 'disabled' : ''} ${loading ? 'loading' : ''}`, // Renamed class
    style: { width, ...(props.style || {}) },
    onClick: onClick as React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>,
    onMouseEnter: onMouseEnter as React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>,
    onMouseLeave: onMouseLeave as React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>,
    onMouseDown: onMouseDown as React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>,
    onMouseUp: onMouseUp as React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>,
    onKeyDown: onKeyDown as React.KeyboardEventHandler<HTMLButtonElement | HTMLAnchorElement>,
    disabled: disabled || loading,
    tabIndex: disabled || loading ? -1 : tabIndex,
    title,
    role,
    ...htmlAttributes,
  };

  const renderContent = () => (
    loading ? (
      <LoadIndicator className="loading-icon" />
    ) : (
      <>
        {iconLeft && <FontAwesomeIcon icon={iconLeft} className="iconLeft" />}
        {content}
        {iconRight && <FontAwesomeIcon icon={iconRight} className="iconRight" />}
      </>
    )
  );

  if ('href' in props && props.href !== undefined) {
    const { href, ...rest } = props as AnchorProps;
    return (
      <a href={href} {...commonProps} {...rest}>
        {renderContent()}
      </a>
    );
  } else if ('to' in props && props.to !== undefined) {
    const { to, ...rest } = props as RouterLinkProps;
    return (
      <Link to={to} {...commonProps} {...rest}>
        {renderContent()}
      </Link>
    );
  } else {
    const { type = 'button', ...rest } = props as ButtonProps;
    return (
      <button type={type} {...commonProps} {...rest}>
        {renderContent()}
      </button>
    );
  }
};

export default Button;


